'use client';

import type { CalendarEvent, LensId, EntityOption } from '@/lib/types';
import type { TickerItem } from '@/lib/event-filters';
import type { TopPage } from '@/lib/related-pages';
import { useCalendarViewLogic } from '@/hooks/useCalendarViewLogic';
import { CalendarToolbar } from './CalendarToolbar';
import { LandingNav } from '../layout/LandingNav';
import { LandingHero } from '../landing/LandingHero';
import { LandingValueProps } from '../landing/LandingValueProps';
import { LandingShowcase } from '../landing/LandingShowcase';
import { LandingSocialProof } from '../landing/LandingSocialProof';
import { LandingExploreStrip } from '../landing/LandingExploreStrip';
import { ArchetypeRouter } from '../landing/ArchetypeRouter';
import { HomepageFaq } from '../landing/HomepageFaq';
import { SourcesBlock } from '../landing/SourcesBlock';
import { LandingCountryDirectory } from '../landing/LandingCountryDirectory';
import { LandingFooter } from '../layout/LandingFooter';
import { WorldEventsTicker } from '../nav/WorldEventsTicker';

interface LandingCalendarViewProps {
  lens: LensId;
  entity?: string;
  entities: Record<LensId, EntityOption[]>;
  lang?: string;
  contentAfterShowcase?: React.ReactNode;
  events?: CalendarEvent[];
  topPages?: TopPage[];
  tickerItems?: TickerItem[];
  localTickerItems?: TickerItem[];
  eventSlugMap?: Record<string, string>;
}

export function LandingCalendarView({
  lens,
  entity = '',
  entities,
  lang = 'en',
  contentAfterShowcase,
  events: eventsProp,
  topPages,
  tickerItems,
  localTickerItems,
  eventSlugMap,
}: LandingCalendarViewProps) {
  const {
    year,
    events,
    availableYears,
    longWeekendHighlights,
    longWeekends,
    entityLabel,
    entityIcon,
    langAlternates,
    previewSlug,
    navConfig,
  } = useCalendarViewLogic({
    lens,
    entity,
    entities,
    lang,
    mode: 'landing',
    events: eventsProp,
  });

  return (
    <div className="flex min-h-screen flex-col bg-surface-root text-text-primary">
      <LandingNav
        lang={lang}
        langAlternates={langAlternates}
        activeCountryCode={lens === 'country' ? entity : undefined}
        showLanguage={navConfig.showLanguage}
        showCountry={navConfig.showCountry}
        showCity={navConfig.showCity}
        showBrowse={navConfig.showBrowse}
        showSearch={navConfig.showSearch}
      />

      {tickerItems && tickerItems.length > 0 && (
        <WorldEventsTicker items={tickerItems} localItems={localTickerItems} lang={lang} />
      )}

      <main id="main-content" className={
        tickerItems && tickerItems.length > 0
          ? localTickerItems && localTickerItems.length > 0
            ? 'pt-[5.75rem]'
            : 'pt-[4.75rem]'
          : 'pt-14'
      }>
        <LandingHero
          entityIcon={entityIcon}
          entityLabel={entityLabel}
          year={year}
          langAlternates={langAlternates}
          lang={lang}
          events={events}
        />

        <LandingExploreStrip lang={lang} />

        <ArchetypeRouter lang={lang} />

        <LandingCountryDirectory lang={lang} />

        <LandingValueProps />

        <div className="mx-auto max-w-2xl px-4 pb-2">
          <CalendarToolbar availableYears={availableYears} />
        </div>

        <LandingShowcase
          entityIcon={entityIcon}
          entityLabel={entityLabel}
          year={year}
          events={events}
          lens={lens}
          lang={lang}
          highlightDates={longWeekendHighlights}
          longWeekends={longWeekends}
          eventSlugMap={eventSlugMap}
          preview
          previewHref={previewSlug ? `/${previewSlug}` : undefined}
        />

        {contentAfterShowcase}

        <LandingSocialProof eventCount={events.length} />

        <HomepageFaq lang={lang} />

        <SourcesBlock lang={lang} />

        <LandingFooter topPages={topPages} lang={lang} />
      </main>
    </div>
  );
}
